// Primitive · Button
//
// Editorial-token wrapper around AntD Button. The contract follows the
// design-system convention: `ButtonInput` (data), `ButtonOutput` (events),
// `ButtonActions` (configurable affordances). Button is an atom — no
// side-actions menu — so `ButtonActions` is intentionally empty.

import type { CSSProperties, MouseEvent, ReactNode, Ref } from "react";
import { Button as AntButton } from "antd";
import type { ButtonProps as AntButtonProps } from "antd";

// ─── Inputs (data props — the model) ─────────────────────────────────────

/**
 * Variants:
 * - `primary`     — solid blue. Affirmative action (Save, Apply, Sync).
 * - `default`     — white outline. Neutral / cancel.
 * - `danger`      — red outline. Cautionary; *most* destructive actions in
 *                   the editorial design use this (Odepnij, Usuń kontakt).
 * - `destructive` — solid red. Only for irreversible confirms with no undo
 *                   (Delete invoice, Drop migration). Use sparingly — visual
 *                   noise.
 * - `text`        — text-only neutral.
 * - `link`        — text-only blue.
 */
export type ButtonVariant =
  | "primary"
  | "default"
  | "danger"
  | "destructive"
  | "text"
  | "link";
export type ButtonSize    = "sm" | "md" | "lg";

export interface ButtonInput {
  /** Visible label. */
  label: ReactNode;
  /** Visual variant — default = neutral outline. `primary` for the
   *  affirmative action, `danger` for destructive, `text`/`link` for
   *  inline. */
  variant?: ButtonVariant;
  /** Size of the button. Default `md`. */
  size?: ButtonSize;
  /** Leading icon. */
  icon?: ReactNode;
  /** When `true`, renders a spinner and disables interaction. */
  loading?: boolean;
  /** When `true`, button is non-interactive and visually muted. */
  disabled?: boolean;
  /** Take the full width of the parent container. */
  block?: boolean;
  /** HTML `type` for forms. Default `"button"`. */
  htmlType?: "button" | "submit" | "reset";
  /** ARIA label override (use when `label` is icon-only or when more
   *  context is needed for screen readers). */
  ariaLabel?: string;
  /** Inline style escape hatch — prefer composing via tier patterns. */
  style?: CSSProperties;
  /** className escape hatch. */
  className?: string;
  /** Optional `data-testid` applied to the underlying button element for
   *  e2e scoping. Atoms don't auto-emit; consumers set this when the
   *  button is the canonical action of a parent primitive (e.g.
   *  `modal-action-{id}`, `toolbar-action-{key}`). */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface ButtonOutput {
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Underlying button element (React 19 ref-as-prop). AntD overlay wrappers
   *  (Popconfirm / Tooltip / Dropdown) inject this into their trigger child to
   *  find the DOM node they position against — dropping it makes the overlay
   *  fail silently. */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
}

// ─── Side actions ────────────────────────────────────────────────────────

// Button is an atom — no configurable side-actions. Per the CLAUDE.md
// convention, the empty `ButtonActions` interface is dropped rather than
// kept as a placeholder (an empty intersection collapses the type).

export type ButtonProps = ButtonInput & ButtonOutput;

// ─── Variant + size mapping ──────────────────────────────────────────────

const VARIANT_TO_ANTD: Record<
  ButtonVariant,
  Pick<AntButtonProps, "type" | "danger">
> = {
  primary:     { type: "primary" },
  default:     { type: "default" },
  danger:      { type: "default", danger: true }, // outline red — editorial default
  destructive: { type: "primary", danger: true }, // solid red — irreversible only
  text:        { type: "text" },
  link:        { type: "link" },
};

const SIZE_TO_ANTD: Record<ButtonSize, AntButtonProps["size"]> = {
  sm: "small",
  md: "middle",
  lg: "large",
};

// ─── Implementation ──────────────────────────────────────────────────────

// A `label` that renders no visible text (null/undefined/empty/whitespace)
// leaves the icon as the button's sole accessible name — keep it in the a11y
// tree. Anything else counts as a visible label.
function hasVisibleLabel(label: ReactNode): boolean {
  return typeof label === "string"
    ? label.trim().length > 0
    : label != null && label !== false;
}

export function Button({
  label,
  variant = "default",
  size = "md",
  icon,
  loading,
  disabled,
  block,
  htmlType = "button",
  ariaLabel,
  style,
  className,
  testId,
  onClick,
  ref,
  ...injected
}: ButtonProps) {
  const variantProps = VARIANT_TO_ANTD[variant];

  // When a visible label exists the icon is decorative — hide it from the a11y
  // tree so the accessible name is exactly the label ("Edytuj", not
  // "edit Edytuj"; `@ant-design/icons` renders `<span role="img"
  // aria-label="…">`). Icon-only buttons keep the icon in the tree so their
  // name still comes from the icon (or `ariaLabel`) — stripping it there would
  // leave them nameless.
  const iconWithLabel =
    icon != null && hasVisibleLabel(label)
      ? <span aria-hidden="true">{icon}</span>
      : icon;

  return (
    <AntButton
      // Overlay-injected trigger props (Popconfirm/Tooltip/Dropdown clone
      // their child with extra handlers) — forwarded first so the explicit
      // props below stay authoritative.
      {...(injected as AntButtonProps)}
      ref={ref}
      {...variantProps}
      size={SIZE_TO_ANTD[size]}
      icon={iconWithLabel}
      loading={loading}
      disabled={disabled}
      block={block}
      htmlType={htmlType}
      aria-label={ariaLabel}
      onClick={onClick}
      style={style}
      className={className}
      data-testid={testId}
    >
      {label}
    </AntButton>
  );
}
